import { useCallback, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router';

import { useCurrentUser } from '@core/auth';
import { useMediaQuery } from '@core/hooks/useMediaQuery';
import { LiveTrackingProvider } from '@core/live';
import { Sheet } from '@core/ui';
import { cn } from '@core/utils/cn';

import { ShellInsetsProvider } from './shellInsets';
import { SideNav } from './SideNav';
import { SideNavFooter } from './SideNavFooter';
import { TopBar } from './TopBar';

const COLLAPSED_KEY = 'fleettrace.nav.collapsed';

/** Rail width plus the margin on each side: what a page must clear to stay readable. */
const RAIL_CLEARANCE_PX = { expanded: 248, collapsed: 88 } as const;

/** Routes whose content is meant to run underneath the floating rail: the map screens. */
const FULL_BLEED_ROUTES = new Set(['/', '/geofences']);

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * The authenticated frame: a glass rail floating over the content, and a scrollable
 * content region beneath it.
 *
 * <p><strong>The rail floats rather than sitting in the layout flow, and that is the
 * whole point of the material.</strong> Glass over a static background is just
 * translucent grey -- it costs a `backdrop-filter` and returns nothing. Letting the fleet
 * map run edge to edge underneath gives the rail something worth refracting, which is
 * also how Maps renders its sidebar.
 *
 * <p>The cost is that a full-bleed page can no longer treat its own box as the visible
 * area, so the rail's clearance is published through `ShellInsetsProvider` and anything
 * that must stay readable offsets by it. Ordinary scrolling pages get the clearance as
 * padding instead and never learn about any of this.
 *
 * <p>`min-h-0` on the content row is what lets a child scroll instead of growing the page
 * -- without it the fleet map cannot fill the viewport and a long list pushes the body.
 */
export function AppShell() {
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { pathname } = useLocation();
  const me = useCurrentUser();

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(COLLAPSED_KEY, String(next));
      } catch {
        // A preference that does not persist is better than a crash.
      }
      return next;
    });
  }, []);

  const railClearance = collapsed ? RAIL_CLEARANCE_PX.collapsed : RAIL_CLEARANCE_PX.expanded;
  const bleed = FULL_BLEED_ROUTES.has(pathname);

  // Below lg the rail is a sheet, not a floating panel, so nothing is covered.
  const isDesktopRail = useMediaQueryLg();
  const insets = useMemo(
    () => ({ left: bleed && isDesktopRail ? railClearance : 0 }),
    [bleed, isDesktopRail, railClearance],
  );

  const shell = (
    <ShellInsetsProvider value={insets}>
      <div className="bg-bg text-fg relative h-dvh overflow-hidden">
        {/* Content first in the DOM and beneath in z-order: the rail refracts it. */}
        <div className="absolute inset-0 flex flex-col">
          {/*
              Only below lg, where it carries the navigation trigger. On desktop the rail
              is always visible and the page supplies its own header, so a full-width bar
              holding nothing is 56px of dead chrome above every screen.
            */}
          <div className="lg:hidden">
            <TopBar onOpenNav={() => setNavOpen(true)} title="FleetTrace" />
          </div>
          <main
            className={cn(
              'min-h-0 min-w-0 flex-1',
              bleed ? 'overflow-hidden' : 'overflow-y-auto',
              // A bleeding page clears the rail itself, where it can do so per element.
              !bleed && 'lg:pl-[var(--rail-clearance)]',
            )}
            style={{ '--rail-clearance': `${railClearance}px` } as React.CSSProperties}
          >
            <Outlet />
          </main>
        </div>

        <aside
          className={cn(
            'absolute inset-y-3 left-3 z-30 hidden flex-col overflow-hidden lg:flex',
            'glass-chrome rounded-sheet',
            // Width, not display, so the rail keeps its scroll position and the content
            // reflows rather than remounting.
            'ease-out-quart transition-[width] duration-200 motion-reduce:transition-none',
            collapsed ? 'w-16' : 'w-56',
          )}
        >
          <div className="border-separator flex h-14 shrink-0 items-center border-b px-3">
            <span
              className={cn(
                'truncate text-[17px] font-semibold tracking-[-0.01em]',
                collapsed && 'w-full text-center text-[15px]',
              )}
            >
              {collapsed ? 'FT' : 'FleetTrace'}
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <SideNav collapsed={collapsed} />
          </div>

          <SideNavFooter collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
        </aside>

        {/* Below lg the rail becomes a sheet rather than shrinking to a strip: 64px of
              icons on a phone costs more width than it earns. */}
        <Sheet open={navOpen} onOpenChange={setNavOpen}>
          <Sheet.Content side="left" className="lg:hidden">
            <Sheet.Header title="FleetTrace" />
            <Sheet.Body className="px-0">
              <SideNav onNavigate={() => setNavOpen(false)} />
            </Sheet.Body>
            <SideNavFooter
              collapsed={false}
              onToggleCollapsed={() => setNavOpen(false)}
              onNavigate={() => setNavOpen(false)}
            />
          </Sheet.Content>
        </Sheet>
      </div>
    </ShellInsetsProvider>
  );

  // LiveTrackingProvider lives HERE rather than at the root: it opens an SSE stream,
  // which needs both a session and an organization. Mounted higher it would connect on
  // the sign-in page, with no identity to scope the stream to -- and for the same reason
  // it is skipped for a platform admin outside every organization, whose ticket request
  // could only ever be refused NO_ORGANIZATION, on a backoff, forever.
  return me.organization ? <LiveTrackingProvider>{shell}</LiveTrackingProvider> : shell;
}

/** Tailwind's `lg`, the breakpoint where the rail stops being a sheet and starts floating. */
function useMediaQueryLg(): boolean {
  return useMediaQuery('(min-width: 1024px)');
}
